import React, { useState, useCallback, useEffect, useRef } from 'react';

interface Pan {
  x: number;
  y: number;
}

interface UseCanvasInteractionsReturn {
  zoom: number;
  pan: Pan;
  isPanning: boolean;
  setZoom: (zoom: number) => void;
  setPan: (pan: Pan) => void;
  handleCanvasMouseDown: (e: React.MouseEvent, onBackgroundClick?: () => void) => void;
  handleZoomIn: () => void;
  handleZoomOut: () => void;
  handleResetView: () => void;
  handleWheel: (e: React.WheelEvent) => void;
}

export const useCanvasInteractions = (
  canvasRef: React.RefObject<HTMLDivElement>
): UseCanvasInteractionsReturn => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Pan>({ x: 350, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Pan>({ x: 0, y: 0 });

  // Use refs to store current values to prevent stale closures
  const panRef = useRef(pan);
  const panStartRef = useRef(panStart);
  const isPanningRef = useRef(isPanning);

  // Update refs when state changes
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    panStartRef.current = panStart;
  }, [panStart]);

  useEffect(() => {
    isPanningRef.current = isPanning;
  }, [isPanning]);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent, onBackgroundClick?: () => void) => {
    const target = e.target as HTMLElement;
    const isNodeElement = target.closest('.workflow-node') || target.closest('button');

    if (!isNodeElement) {
      onBackgroundClick?.();
      setIsPanning(true);
      setPanStart({ x: e.clientX - panRef.current.x, y: e.clientY - panRef.current.y });
    }
  }, []);

  // Create stable event handlers that don't change on every render
  const handleCanvasMouseMove = useCallback((e: MouseEvent) => {
    if (isPanningRef.current) {
      setPan({
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y
      });
    }
  }, []);

  const handleCanvasMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  }, []);

  const handleResetView = useCallback(() => {
    setZoom(1);
    setPan({ x: 350, y: 0 });
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    if (e.ctrlKey || e.metaKey) {
      // Zoom with Ctrl/Cmd + scroll
      const currentZoom = zoom;
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.min(Math.max(currentZoom + delta, 0.5), 2);

      // Zoom towards mouse position
      const zoomFactor = newZoom / currentZoom;
      const currentPan = panRef.current;
      setPan({
        x: currentPan.x + (mouseX - currentPan.x) * (1 - zoomFactor),
        y: currentPan.y + (mouseY - currentPan.y) * (1 - zoomFactor)
      });
      setZoom(newZoom);
    } else {
      // Pan with scroll
      const currentPan = panRef.current;
      setPan({
        x: currentPan.x - e.deltaX * 0.5,
        y: currentPan.y - e.deltaY * 0.5
      });
    }
  }, [zoom, canvasRef]);

  // Mouse event listeners for panning
  useEffect(() => {
    if (isPanning) {
      document.addEventListener('mousemove', handleCanvasMouseMove);
      document.addEventListener('mouseup', handleCanvasMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleCanvasMouseMove);
        document.removeEventListener('mouseup', handleCanvasMouseUp);
      };
    }
  }, [isPanning, handleCanvasMouseMove, handleCanvasMouseUp]);

  return {
    zoom,
    pan,
    isPanning,
    setZoom,
    setPan,
    handleCanvasMouseDown,
    handleZoomIn,
    handleZoomOut,
    handleResetView,
    handleWheel
  };
};